/**
 * Stale-while-revalidate snapshot for /p/dashboard/home KPIs + recent feed.
 * Hydrate synchronously on mount so the page feels instant; always revalidate in background.
 */

const CACHE_PREFIX = "hr_home_dashboard_v1_";
/** Keep snapshots for display even if older; always network-revalidate on mount. */
const MAX_AGE_MS = 1000 * 60 * 60 * 12; // 12h

function cacheKey(agentKey) {
  return `${CACHE_PREFIX}${String(agentKey || "anon").trim().toLowerCase()}`;
}

export function resolveHomeDashboardCacheAgentKey(userInfo, userDetails) {
  const email =
    userInfo?.email ||
    userDetails?.email ||
    userInfo?.Email ||
    userDetails?.Email ||
    "";
  const id = userInfo?.id ?? userInfo?.agentId ?? userDetails?.id ?? "";
  const e = String(email).trim().toLowerCase();
  if (e) return e;
  const idStr = String(id).trim();
  return idStr || "anon";
}

/**
 * @returns {{
 *   savedAt: number,
 *   stats: object,
 *   homeWalletSnapshot: object|null,
 *   ordersForCard: array|null,
 * } | null}
 */
export function readHomeDashboardCache(agentKey) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(agentKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const savedAt = Number(parsed.savedAt) || 0;
    if (!savedAt || Date.now() - savedAt > MAX_AGE_MS) return null;
    return {
      savedAt,
      stats: parsed.stats && typeof parsed.stats === "object" ? parsed.stats : null,
      homeWalletSnapshot:
        parsed.homeWalletSnapshot && typeof parsed.homeWalletSnapshot === "object"
          ? parsed.homeWalletSnapshot
          : null,
      ordersForCard: Array.isArray(parsed.ordersForCard)
        ? parsed.ordersForCard
        : null,
    };
  } catch {
    return null;
  }
}

/** Slim wallet snapshot — totals only (avoid huge line arrays in localStorage). */
export function slimHomeWalletSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return null;
  const summary = snapshot.walletSummary || {};
  return {
    possibleProfit: snapshot.possibleProfit ?? null,
    availableRowsTotal: snapshot.availableRowsTotal ?? null,
    walletSummary: {
      futureTotal: Number(summary.futureTotal) || 0,
      availableTotal: Number(summary.availableTotal) || 0,
      historyTotal: Number(summary.historyTotal) || 0,
      withdrawableTotal: Number(summary.withdrawableTotal) || 0,
    },
  };
}

export function writeHomeDashboardCache(agentKey, payload) {
  if (typeof window === "undefined") return;
  try {
    const body = {
      savedAt: Date.now(),
      stats: {
        futureProfit: payload?.stats?.futureProfit ?? null,
        availableProfit: payload?.stats?.availableProfit ?? null,
        historyProfit: payload?.stats?.historyProfit ?? null,
        pendingOrders: payload?.stats?.pendingOrders ?? null,
        openOrders: payload?.stats?.openOrders ?? null,
      },
      homeWalletSnapshot: slimHomeWalletSnapshot(payload?.homeWalletSnapshot),
      ordersForCard: Array.isArray(payload?.ordersForCard)
        ? payload.ordersForCard.slice(0, 15)
        : null,
    };
    window.localStorage.setItem(cacheKey(agentKey), JSON.stringify(body));
  } catch {
    // quota / private mode — ignore
  }
}
